import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './app/store';
import { getUserProfile, setToken } from './features/auth/authSlice';
import { AppDispatch, RootState } from './app/store';
import { setTokenUpdateCallback } from './config/api';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import DashboardLayout from './components/dashboard/DashboardLayout';
import OverviewPage from './pages/dashboard/OverviewPage';
import FeedbackPage from './pages/dashboard/FeedbackPage';
import AnalyticsPage from './pages/dashboard/AnalyticsPage';
import ConversationList from './pages/ConversationList';
import GoogleReviewsPage from './pages/dashboard/GoogleReviewsPage';
import GuestCommentCardPage from './pages/dashboard/GuestCommentCardPage';
import TikTokCommentsPage from './pages/dashboard/TikTokCommentsPage';
import DeveloperFeedbackPage from './pages/dashboard/DeveloperFeedbackPage';
import TemplateManagementPage from './pages/dashboard/TemplateManagementPage';
import PrivateRoute from './components/auth/PrivateRoute';
import FeedbackDashboard from './pages/dashboard/FeedbackDashboard';
import UserManagementPage from './pages/dashboard/UserManagementPage';
import OutletManagementPage from './pages/dashboard/OutletManagementPage';
import BrandManagementPage from './pages/dashboard/BrandManagementPage';
import CrawlManagementPage from './pages/dashboard/CrawlManagementPage';
import UserActivityPage from './pages/dashboard/UserActivityPage';
import NavigationLogger from './components/NavigationLogger';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#E31837', // Boga Group red
      light: '#FF4D4D',
      dark: '#B31212',
    },
    secondary: {
      main: '#333333',
      light: '#666666',
      dark: '#1A1A1A',
    },
    background: {
      default: '#f8f8f8',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});

// Auth initialization component
const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, token } = useSelector((state: RootState) => state.auth);
  
  useEffect(() => {
    // Set up token update callback
    setTokenUpdateCallback((newToken: string | null) => {
      dispatch(setToken(newToken));
    });

    // Check for token in localStorage
    const savedToken = localStorage.getItem('token');
    
    // If we have a token but no user data, fetch user profile
    if (savedToken && !user) {
      console.log('Initializing user session from token');
      dispatch(getUserProfile());
    }
  }, [dispatch, user, token]);
  
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <AuthInitializer>
            <NavigationLogger />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />

              {/* Protected dashboard routes */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<OverviewPage />} />
                <Route path="feedback" element={<FeedbackDashboard />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="conversations" element={<ConversationList />} />
                <Route path="google-reviews" element={<GoogleReviewsPage />} />
                <Route path="guest-comment-cards" element={<GuestCommentCardPage />} />
                <Route path="tiktok-comments" element={<TikTokCommentsPage />} />
                <Route path="developer-feedback" element={<DeveloperFeedbackPage />} />
                <Route path="templates" element={<TemplateManagementPage />} />
                <Route path="templates" element={<TemplateManagementPage />} />
                <Route path="user-management" element={<UserManagementPage />} />
                <Route path="outlet-management" element={<OutletManagementPage />} />
                <Route path="brand-management" element={<BrandManagementPage />} />
                <Route path="crawl-management" element={<CrawlManagementPage />} />
                <Route path="user-activity" element={<UserActivityPage />} />
              </Route>

              {/* Fallback route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthInitializer>
        </BrowserRouter>
      </ThemeProvider>
    </Provider>
  );
};

export default App;
